'use client';

import { unstable_rethrow } from 'next/navigation';
import { FormEvent, useCallback, useState } from 'react';
import { usePublicConfigurationContext } from 'runtime-configuration';

import { RecaptchaFallbackDecision } from './RecaptchaFallbackDecision';
import { useIsDatadogSynthetic } from './useIsDatadogSynthetic';
import { useRecaptchaV2Token } from './v2/useRecaptchaV2Token';
import { RecaptchaV3Action } from './v3/RecaptchaV3Action';
import { useRecaptchaV3Callback } from './v3/useRecaptchaV3Callback';

const V2_UNSOLVED_MESSAGE = 'Please complete the reCAPTCHA challenge to continue.';

export interface FormWithRecaptchaConfig {
    action: RecaptchaV3Action;
    // Performs the real work (server action / API call, redirect, error display) and reports
    // back whether a v2 challenge is now required.
    onSubmit: (
        formData: FormData,
        recaptcha: { token: string | undefined; version: 'v2' | 'v3' },
    ) => Promise<RecaptchaFallbackDecision>;
    // Unexpected thrown error (e.g. network). The form surfaces a generic error to the user.
    onError?: () => void;
    // User submitted in v2 mode without solving the checkbox.
    onValidationError?: (message: string) => void;
}

export interface UseFormWithRecaptcha {
    handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
    v2Challenge: {
        visible: boolean;
        resetKey: number;
        onChange: (token: string | null) => void;
    };
    canSubmit: boolean;
}

/**
 * The one reCAPTCHA hook for any form: runs invisible v3 first and falls back to the v2 checkbox
 * when v3 fails — either client-side (the SDK is unavailable / yields no token) or because the
 * caller's onSubmit reports the server rejected the token. Backend-agnostic: the caller maps its
 * own result to a RecaptchaFallbackDecision.
 */
export function useFormWithRecaptcha({
    action,
    onSubmit,
    onError,
    onValidationError,
}: FormWithRecaptchaConfig): UseFormWithRecaptcha {
    const { recaptchaDisabled } = usePublicConfigurationContext();
    const isSynthetic = useIsDatadogSynthetic();
    const executeV3 = useRecaptchaV3Callback(action);
    const { token: v2Token, onChange, reset, resetKey } = useRecaptchaV2Token();
    // Single source of truth for the one-way v3 → v2 escalation. Challenge visibility is derived
    // from it rather than tracked separately, so the two can never disagree.
    const [mode, setMode] = useState<'v3' | 'v2'>('v3');
    const challengeVisible = mode === 'v2';

    // Datadog synthetics can't solve the v2 checkbox and the server bypasses verification for
    // them, so treat them like recaptcha being disabled.
    const bypass = recaptchaDisabled || isSynthetic;

    const showChallenge = useCallback((): void => setMode('v2'), []);

    const submitBypassed = useCallback(
        async (formData: FormData): Promise<void> => {
            await onSubmit(formData, { token: undefined, version: 'v3' });
        },
        [onSubmit],
    );

    const submitWithV3 = useCallback(
        async (formData: FormData): Promise<void> => {
            const token = await executeV3();
            if (!token) {
                // v3 SDK missing or execute() produced no token — escalate without a round-trip.
                showChallenge();
                return;
            }
            const decision = await onSubmit(formData, { token, version: 'v3' });
            if (decision === RecaptchaFallbackDecision.NeedsV2Challenge) {
                showChallenge();
            }
        },
        [executeV3, showChallenge, onSubmit],
    );

    const submitWithV2 = useCallback(
        async (formData: FormData): Promise<void> => {
            if (v2Token === null) {
                onValidationError?.(V2_UNSOLVED_MESSAGE);
                return;
            }
            const decision = await onSubmit(formData, { token: v2Token, version: 'v2' });
            // A v2 token is single-use. Refresh the checkbox only when the server consumed it and the
            // user stays on the form: NeedsV2Challenge (rejected) or RefreshV2Challenge (verified,
            // then the submit failed for another reason). Leave it on Handled — the submit either
            // succeeded, or a client-side error bailed before spending the token, so the solved
            // checkbox is still valid for the retry.
            if (
                decision === RecaptchaFallbackDecision.NeedsV2Challenge ||
                decision === RecaptchaFallbackDecision.RefreshV2Challenge
            ) {
                reset();
            }
        },
        [v2Token, reset, onSubmit, onValidationError],
    );

    const handleSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>): void => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);

            let run: (data: FormData) => Promise<void>;
            if (bypass) {
                run = submitBypassed;
            } else if (mode === 'v3') {
                run = submitWithV3;
            } else {
                run = submitWithV2;
            }

            run(formData).catch((error: unknown) => {
                // Next.js redirect/notFound control-flow errors must bubble to the router.
                unstable_rethrow(error);
                onError?.();
            });
        },
        [bypass, mode, submitBypassed, submitWithV3, submitWithV2, onError],
    );

    return {
        handleSubmit,
        v2Challenge: { visible: challengeVisible, resetKey, onChange },
        canSubmit: !challengeVisible || bypass || v2Token !== null,
    };
}
